import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { projectsApi } from '@/api/projects.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { usePagination } from '@/hooks/usePagination';
import { useProjectList } from '@/modules/projects/shared/useProjectList';
import type { ProjectIssue } from '@/types/projects';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function ProjectsIssuesPage() {
  const pagination = usePagination();
  const { projects } = useProjectList();
  const [data, setData] = useState<ProjectIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ projectId: 0, title: '', description: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsApi.listIssues({ page: pagination.page, size: pagination.size });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, [pagination]);

  useEffect(() => { void load(); }, [load]);

  const columns: Column<ProjectIssue>[] = [
    { key: 'title', header: 'Title' },
    { key: 'projectId', header: 'Project' },
    { key: 'sourceModule', header: 'Source' },
  ];

  const create = async () => {
    try {
      await projectsApi.createIssue(form);
      notify.success('Issue created');
      setOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Create failed');
    }
  };

  return (
    <div>
      <PageHeader title="Issues" description="Bugs and Desk ticket escalations (auto-linked from Desk)"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Projects', to: '/projects' }, { label: 'Issues' }]}
        actions={<Button onClick={() => setOpen(true)}><AddIcon fontSize="small" /> New issue</Button>} />
      <DataTable columns={columns} data={data} keyField="id" loading={loading}
        page={pagination.page} totalPages={pagination.totalPages} totalElements={pagination.totalElements} onPageChange={pagination.setPage} />
      <Modal open={open} onClose={() => setOpen(false)} title="Create issue" footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={() => void create()}>Save</Button></>}>
        <div className="space-y-3">
          <Select
            placeholder="Select project"
            value={String(form.projectId || '')}
            onChange={(v) => setForm({ ...form, projectId: v ? Number(v) : 0 })}
            options={projects.map((p) => ({ value: String(p.id), label: p.projectName }))}
          />
          <Input label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </Modal>
    </div>
  );
}
